// Apex Business Intelligence — Round 7 (additive)
// Billing & Subscription page.

import React, { useEffect, useState, useCallback } from "react";
import { toast } from "./intelligence";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "apex_auth_token";
const auth = () => { const t = localStorage.getItem(TOKEN_KEY); return t ? { Authorization: "Bearer " + t } : {}; };
const j = (path, opts = {}) =>
  fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", ...auth(), ...(opts.headers || {}) } })
    .then(async r => { const data = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(data.detail || r.statusText), { data, status: r.status }); return data; });

const labelStyle = { fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text3)", marginBottom: 4, display: "block" };
const card = { background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 8, padding: 18, marginBottom: 14 };
const subhead = { fontFamily: "var(--rajdhani)", fontSize: 14, fontWeight: 700, marginBottom: 10, letterSpacing: 0.5 };
const tableHead = { fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "var(--text3)", textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border)" };
const tableCell = { fontSize: 12, padding: "8px 10px", borderBottom: "1px solid var(--border)" };
const pill = (bg, text = "#fff") => ({ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 10, letterSpacing: 0.5, background: bg, color: text });

// ───────────────────────────────────────────────────────────────
// BILLING PAGE
// ───────────────────────────────────────────────────────────────
export function BillingPage() {
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [revenueShare, setRevenueShare] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [leadQty, setLeadQty] = useState(1);
  const [leadId, setLeadId] = useState("");
  const [rsAmount, setRsAmount] = useState("");
  const [rsJobId, setRsJobId] = useState("");
  const [rsDesc, setRsDesc] = useState("");

  const load = useCallback(async () => {
    try {
      const p = await j("/api/iv/r7/plans");
      setPlans(p.plans || []);
      setAddons(p.addons || []);
      setRevenueShare(p.revenue_share || null);
      setBootstrapped(!!p.bootstrapped);
    } catch (e) { /* surfaced below */ }
    try { const s = await j("/api/iv/r7/subscription"); setSub(s); } catch {}
    try { const i = await j("/api/iv/r7/invoices"); setInvoices(i.invoices || []); } catch {}
    try { const ph = await j("/api/iv/r7/payments/history?limit=25"); setPayments(ph.payments || []); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  // After Checkout, frontend lands on /?billing_session=cs_xxx — poll status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("billing_session");
    const cancelled = params.get("billing_cancelled");
    if (cancelled) {
      toast("Checkout cancelled", "err");
      const u = new URL(window.location);
      u.searchParams.delete("billing_cancelled"); window.history.replaceState({}, "", u);
      return;
    }
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await j(`/api/iv/r7/checkout/status/${sid}`);
        if (r.payment_status === "paid") {
          toast("Subscription activated");
          setPolling(false);
          const u = new URL(window.location);
          u.searchParams.delete("billing_session"); window.history.replaceState({}, "", u);
          load();
          return;
        }
        if (r.status === "expired") {
          toast("Checkout session expired", "err");
          setPolling(false);
          return;
        }
        if (attempts < 8) setTimeout(tick, 2500);
        else { setPolling(false); toast("Status check timed out — refresh once payment clears.", "err"); }
      } catch (e) { setPolling(false); toast(e.message, "err"); }
    };
    tick();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doBootstrap = async () => {
    setBusy(true);
    try { await j("/api/iv/r7/bootstrap", { method: "POST" }); toast("Stripe products + prices bootstrapped"); load(); }
    catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  };

  const subscribe = async (planKey) => {
    if (planKey === "enterprise") return toast("Enterprise is Contact Sales — please reach out.", "err");
    setBusy(true);
    try {
      const r = await j("/api/iv/r7/checkout", { method: "POST", body: JSON.stringify({ plan_key: planKey, origin_url: window.location.origin }) });
      if (r.url) window.location.href = r.url;
    } catch (e) { toast(e.message, "err"); setBusy(false); }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await j("/api/iv/r7/portal", { method: "POST", body: JSON.stringify({ return_url: window.location.origin }) });
      if (r.url) window.open(r.url, "_blank");
    } catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  };

  const subscribeAddon = async (addonKey) => {
    setBusy(true);
    try {
      const r = await j("/api/iv/r7/addon/checkout", { method: "POST", body: JSON.stringify({ addon_key: addonKey, origin_url: window.location.origin }) });
      if (r.url) window.location.href = r.url;
    } catch (e) { toast(e.message, "err"); setBusy(false); }
  };

  const buyLeadFee = async () => {
    const qty = parseInt(leadQty, 10);
    if (!qty || qty < 1) return toast("Quantity must be ≥ 1", "err");
    setBusy(true);
    try {
      const r = await j("/api/iv/r7/lead-fee/checkout", { method: "POST", body: JSON.stringify({ origin_url: window.location.origin, quantity: qty, lead_id: leadId || null }) });
      if (r.url) window.location.href = r.url;
    } catch (e) { toast(e.message, "err"); setBusy(false); }
  };

  const chargeRevenueShare = async () => {
    const dollars = parseFloat(rsAmount);
    if (!dollars || dollars <= 0) return toast("Enter a dollar amount", "err");
    const cents = Math.round(dollars * 100);
    setBusy(true);
    try {
      const r = await j("/api/iv/r7/revenue-share/checkout", { method: "POST", body: JSON.stringify({
        origin_url: window.location.origin, amount_cents: cents, job_id: rsJobId || null, description: rsDesc || null,
      }) });
      if (r.url) window.location.href = r.url;
    } catch (e) { toast(e.message, "err"); setBusy(false); }
  };

  const currentPlan = sub?.plan_key || "starter";

  return (
    <div data-testid="r7-billing-page" style={{ padding: 24 }}>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={subhead}>Billing & Subscription</div>
            <div style={{ fontSize: 12, color: "var(--text3)" }}>
              Stripe test mode · Plans + prices auto-bootstrapped on demand
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {!bootstrapped && (
              <button data-testid="bootstrap-btn" className="btn btn-primary" onClick={doBootstrap} disabled={busy} style={{ fontSize: 11 }}>
                {busy ? "Bootstrapping…" : "Bootstrap Test Prices"}
              </button>
            )}
            <button data-testid="open-portal-btn" className="btn btn-ghost" onClick={openPortal} disabled={busy || !sub?.subscription} style={{ fontSize: 11 }}>
              Manage Billing →
            </button>
          </div>
        </div>
      </div>

      {polling && (
        <div style={{ ...card, borderColor: "#1d4ed8" }} data-testid="billing-poll-banner">
          <div style={{ fontSize: 12 }}>Confirming your subscription with Stripe… this can take a few seconds.</div>
        </div>
      )}

      <div style={card}>
        <div style={subhead}>Current Plan</div>
        {sub?.subscription ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
            <Stat label="Plan" value={(plans.find(p => p.key === currentPlan)?.name) || currentPlan} color="#1d4ed8" />
            <Stat label="Status" value={sub.subscription.status} color={sub.subscription.status === "active" ? "#16a34a" : "#f59e0b"} />
            <Stat label="Seats" value={sub.seats?.max == null ? "Unlimited" : sub.seats?.max} />
            <Stat label="Renews" value={sub.subscription.current_period_end ? new Date(sub.subscription.current_period_end * 1000).toLocaleDateString() : "—"} />
            <Stat label="Cancel @ End" value={sub.subscription.cancel_at_period_end ? "Yes" : "No"} color={sub.subscription.cancel_at_period_end ? "#cc0000" : "#16a34a"} />
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "var(--text3)" }}>
            No active subscription — currently on free Starter (5 seats). Choose a plan below.
          </div>
        )}
      </div>

      <div style={card}>
        <div style={subhead}>Plans</div>
        {!bootstrapped && (
          <div style={{ fontSize: 11, color: "#f59e0b", marginBottom: 10 }}>
            Click <strong>Bootstrap Test Prices</strong> above to create Stripe products + prices in test mode before subscribing.
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14 }}>
          {plans.map(p => {
            const isCurrent = p.key === currentPlan && sub?.subscription;
            return (
              <div key={p.key} data-testid={`plan-card-${p.key}`} style={{ background: "var(--bg3)", border: `1px solid ${isCurrent ? "#16a34a" : "var(--border)"}`, borderRadius: 8, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontFamily: "var(--rajdhani)", fontSize: 18, fontWeight: 700 }}>{p.name}</div>
                  {isCurrent && <span style={pill("#16a34a")}>CURRENT</span>}
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: "var(--cyan)", margin: "8px 0", fontFamily: "var(--rajdhani)" }}>
                  ${(p.price_cents / 100).toLocaleString()}<span style={{ fontSize: 11, color: "var(--text3)" }}>/{p.interval}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 10 }}>{p.description}</div>
                <ul style={{ paddingLeft: 18, fontSize: 12, marginBottom: 14 }}>
                  {(p.features || []).map(f => <li key={f} style={{ marginBottom: 4 }}>{f}</li>)}
                </ul>
                <button
                  data-testid={`subscribe-btn-${p.key}`}
                  className={isCurrent ? "btn btn-ghost" : "btn btn-primary"}
                  disabled={busy || isCurrent || !bootstrapped || p.key === "enterprise"}
                  onClick={() => subscribe(p.key)}
                  style={{ width: "100%", fontSize: 11 }}
                  title={p.key === "enterprise" ? "Contact Sales" : (isCurrent ? "Current plan" : "")}
                >
                  {isCurrent ? "Current Plan" : p.key === "enterprise" ? "Contact Sales" : (busy ? "Loading…" : "Subscribe")}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div style={card}>
        <div style={subhead}>Add-ons</div>
        {!bootstrapped && (
          <div style={{ fontSize: 11, color: "#f59e0b", marginBottom: 10 }}>
            Bootstrap prices first (button at top of page).
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14 }}>
          {addons.map(a => (
            <div key={a.key} data-testid={`addon-card-${a.key}`} style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontFamily: "var(--rajdhani)", fontSize: 16, fontWeight: 700 }}>{a.name}</div>
                <span style={pill(a.type === "subscription" ? "#1d4ed8" : "#6b7280")}>{a.type === "subscription" ? "MONTHLY" : "ONE-TIME"}</span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "var(--cyan)", margin: "8px 0", fontFamily: "var(--rajdhani)" }}>
                ${(a.price_cents / 100).toFixed(2)}{a.interval && <span style={{ fontSize: 11, color: "var(--text3)" }}>/{a.interval}</span>}
              </div>
              <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 10 }}>{a.description}</div>
              {a.key === "lead_fee" ? (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 6, marginBottom: 8 }}>
                    <div>
                      <label style={labelStyle}>Qty</label>
                      <input data-testid="lead-fee-qty" type="number" min="1" value={leadQty} onChange={e => setLeadQty(e.target.value)} style={{ padding: "8px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 4, color: "var(--text)", fontSize: 12, width: "100%" }} />
                    </div>
                    <div>
                      <label style={labelStyle}>Lead ID (optional)</label>
                      <input data-testid="lead-fee-id" value={leadId} onChange={e => setLeadId(e.target.value)} style={{ padding: "8px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 4, color: "var(--text)", fontSize: 12, width: "100%" }} />
                    </div>
                  </div>
                  <button data-testid="buy-lead-fee-btn" className="btn btn-primary" disabled={busy || !bootstrapped} onClick={buyLeadFee} style={{ width: "100%", fontSize: 11 }}>
                    Charge ${((leadQty || 0) * 2).toFixed(2)}
                  </button>
                </div>
              ) : (
                <button data-testid={`subscribe-addon-${a.key}`} className="btn btn-primary" disabled={busy || !bootstrapped} onClick={() => subscribeAddon(a.key)} style={{ width: "100%", fontSize: 11 }}>
                  Subscribe
                </button>
              )}
            </div>
          ))}
          {revenueShare && (
            <div data-testid="addon-card-revenue_share" style={{ background: "var(--bg3)", border: "1px dashed var(--border)", borderRadius: 8, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontFamily: "var(--rajdhani)", fontSize: 16, fontWeight: 700 }}>{revenueShare.name}</div>
                <span style={pill("#f59e0b")}>VARIABLE</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text3)", margin: "8px 0" }}>{revenueShare.description}</div>
              <label style={labelStyle}>Amount (USD)</label>
              <input data-testid="rs-amount" type="number" step="0.01" min="1" placeholder="0.00" value={rsAmount} onChange={e => setRsAmount(e.target.value)} style={{ padding: "8px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 4, color: "var(--text)", fontSize: 13, width: "100%", marginBottom: 6 }} />
              <label style={labelStyle}>Job ID (optional)</label>
              <input data-testid="rs-job-id" value={rsJobId} onChange={e => setRsJobId(e.target.value)} style={{ padding: "8px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 4, color: "var(--text)", fontSize: 12, width: "100%", marginBottom: 6 }} />
              <label style={labelStyle}>Note</label>
              <input data-testid="rs-desc" value={rsDesc} onChange={e => setRsDesc(e.target.value)} placeholder="e.g. Apex job J-1234 referral 20%" style={{ padding: "8px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 4, color: "var(--text)", fontSize: 12, width: "100%", marginBottom: 8 }} />
              <button data-testid="charge-revenue-share-btn" className="btn btn-primary" disabled={busy || !rsAmount} onClick={chargeRevenueShare} style={{ width: "100%", fontSize: 11 }}>
                Charge ${rsAmount ? Number(rsAmount).toFixed(2) : "0.00"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={card}>
        <div style={subhead}>Payment History ({payments.length})</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr><th style={tableHead}>Kind</th><th style={tableHead}>Amount</th><th style={tableHead}>Reference</th><th style={tableHead}>Status</th><th style={tableHead}>Created</th></tr>
          </thead>
          <tbody>
            {payments.map(p => (
              <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                <td style={tableCell}><span style={pill("#1d4ed8")}>{(p.kind || p.plan_key || "plan").replace("_", " ")}</span></td>
                <td style={tableCell}>${((p.amount_cents || 0) / 100).toFixed(2)}</td>
                <td style={tableCell}>{p.job_id || p.lead_id || p.addon_key || p.plan_key || "—"}</td>
                <td style={tableCell}><span style={pill(p.payment_status === "paid" ? "#16a34a" : p.payment_status === "initiated" ? "#f59e0b" : "#6b7280")}>{p.payment_status}</span></td>
                <td style={tableCell}>{(p.created_at || "").slice(0, 19).replace("T", " ")}</td>
              </tr>
            ))}
            {!payments.length && <tr><td colSpan={5} style={{ ...tableCell, color: "var(--text3)", textAlign: "center" }}>No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={card}>
        <div style={subhead}>Invoices ({invoices.length})</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr><th style={tableHead}>Number</th><th style={tableHead}>Status</th><th style={tableHead}>Amount</th><th style={tableHead}>Period</th><th style={tableHead}>Created</th><th style={tableHead}></th></tr>
          </thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <td style={tableCell}>{inv.number || inv.id?.slice(0, 14)}</td>
                <td style={tableCell}><span style={pill(inv.status === "paid" ? "#16a34a" : inv.status === "open" ? "#f59e0b" : "#6b7280")}>{inv.status}</span></td>
                <td style={tableCell}>${((inv.amount_paid || inv.amount_due || 0) / 100).toFixed(2)} {(inv.currency || "usd").toUpperCase()}</td>
                <td style={tableCell}>{inv.period_start ? `${new Date(inv.period_start * 1000).toLocaleDateString()} → ${new Date(inv.period_end * 1000).toLocaleDateString()}` : "—"}</td>
                <td style={tableCell}>{inv.created ? new Date(inv.created * 1000).toLocaleDateString() : "—"}</td>
                <td style={tableCell}>
                  {inv.hosted_invoice_url && <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{ fontSize: 10 }}>View</a>}
                  {inv.invoice_pdf && <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{ fontSize: 10, marginLeft: 4 }}>PDF</a>}
                </td>
              </tr>
            ))}
            {!invoices.length && <tr><td colSpan={6} style={{ ...tableCell, color: "var(--text3)", textAlign: "center" }}>No invoices yet. They will appear here after your first subscription payment.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Stat = ({ label, value, color = "var(--text)" }) => (
  <div style={{ background: "var(--bg)", padding: 12, borderRadius: 6, border: "1px solid var(--border)" }}>
    <div style={{ fontSize: 10, color: "var(--text3)", letterSpacing: 1 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color, fontFamily: "var(--rajdhani)" }}>{value || "—"}</div>
  </div>
);
